import { useState } from "react";

/**
 * Tooltip component for explaining KPIs and metrics
 */
export default function KPITooltip({ content, children, position = "top" }) {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-slate-800 border-x-transparent border-b-transparent",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-slate-800 border-x-transparent border-t-transparent",
    left: "left-full top-1/2 -translate-y-1/2 border-l-slate-800 border-y-transparent border-r-transparent",
    right: "right-full top-1/2 -translate-y-1/2 border-r-slate-800 border-y-transparent border-l-transparent",
  };

  return (
    <span 
      className="kpi-tooltip-wrapper"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <button
        type="button"
        aria-label="Information"
        className="kpi-tooltip-trigger"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsVisible(!isVisible);
        }}
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M8 7.3v3.4M8 5.5h.01" strokeLinecap="round" />
        </svg>
      </button>
      
      {isVisible && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} w-64 pointer-events-none`}
          role="tooltip"
        >
          <div className="kpi-tooltip-bubble">
            {content}
          </div>
          <div 
            className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`}
          />
        </div>
      )}
      
      <style>{`
        .kpi-tooltip-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .kpi-tooltip-trigger {
          width: 14px;
          height: 14px;
          min-width: 14px;
          padding: 0;
          background: transparent;
          border: none;
          color: rgba(122, 129, 148, 0.6);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: help;
          flex-shrink: 0;
          transition: color 0.2s ease;
        }
        .kpi-tooltip-trigger:hover {
          color: #B5894A;
        }
        .kpi-tooltip-trigger svg {
          width: 14px;
          height: 14px;
          display: block;
        }
        .kpi-tooltip-bubble {
          background: #16233B;
          color: #FFFFFF;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 400;
          font-style: normal;
          letter-spacing: 0;
          line-height: 1.5;
          text-transform: none;
          border-radius: 8px;
          padding: 8px 12px;
          box-shadow: 0 4px 14px rgba(40, 28, 18, 0.25);
        }
      `}</style>
    </span>
  );
}

/**
 * Pre-defined tooltip contents for common KPIs
 */
export const KPI_TOOLTIPS = {
  // Risk scores
  riskScore: "Score de risque d'abandon (0-100%). Plus le score est élevé, plus l'élève risque d'abandonner son projet. Basé sur : inactivité, retards, étapes non complétées.",
  highRisk: "Élèves avec un score de risque ≥ 60%. Nécessitent une attention immédiate et un suivi rapproché.",
  mediumRisk: "Élèves avec un score de risque entre 30% et 59%. À surveiller et encourager régulièrement.",
  lowRisk: "Élèves avec un score de risque < 30%. En bonne voie, progression normale.",
  avgRiskScore: "Moyenne des scores de risque de tous vos élèves. Indicateur global de la santé de votre portefeuille.",
  
  // Progress
  progression: "Pourcentage d'avancement dans le parcours investisseur. Calculé selon les étapes validées sur le total (11 étapes principales).",
  completedSteps: "Nombre d'étapes principales validées par l'élève sur les 11 étapes du parcours investisseur.",
  
  // Activity
  activityMonth: "Nombre de sous-étapes créées ou validées ce mois-ci. Mesure votre implication active auprès des élèves.",
  activityTrend: "Évolution de votre activité (sous-étapes créées) sur les dernières semaines. Permet de visualiser les tendances.",
  stagesValidatedMonth: "Nombre d'étapes principales que vous avez validées ce mois-ci pour vos élèves.",
  
  // Success
  successRate: "Pourcentage d'élèves ayant atteint ou dépassé 8 étapes validées sur 11. Mesure l'efficacité de votre accompagnement.",
  abandonmentRate: "Pourcentage d'élèves inactifs depuis plus de 60 jours. Indicateur de désengagement.",
  
  // Reactivity
  reactivityScore: "Temps moyen de réponse aux messages de vos élèves. Un bon score = réponses rapides (< 24h).",
  
  // Coach stats (admin view)
  coachStudentsCount: "Nombre d'élèves actuellement assignés à ce coach.",
  coachAvgRisk: "Moyenne des scores de risque des élèves de ce coach. Permet de comparer les performances entre coachs.",
  coachSuccessRate: "Taux de réussite du coach : % d'élèves ayant atteint 8+ étapes validées.",
  
  // Admin stats
  totalStudents: "Nombre total d'élèves actifs sur la plateforme.",
  totalCoaches: "Nombre total de coachs actifs sur la plateforme.",
  totalAssignments: "Nombre total d'affectations élève-coach actives.",
  averageProgress: "Progression moyenne de tous les élèves dans leur parcours investisseur.",
  
  // Integration
  integrationDate: "Répartition des élèves selon leur date d'inscription. Permet de suivre les cohortes.",
  
  // Odysboard
  odysboardLinked: "Indique si l'élève a connecté son compte Odysboard pour synchroniser ses données de recherche immobilière.",
  annonces: "Nombre d'annonces immobilières sauvegardées par l'élève dans Odysboard.",
  simulations: "Nombre de simulations de financement réalisées par l'élève.",
  visites: "Nombre de visites de biens programmées ou effectuées.",
  villes: "Nombre de villes ciblées par l'élève pour son investissement.",
};
